import { minutesFromMidnightToTime } from '../../../../utils';
import { getEndDateFieldError, getStartFieldError } from '../../helpers';

/**
 * Calculate the number of weeks between two dates.
 * Uses Math.round(diffDays/7) - e.g. 6 days rounds to 1 week, 7–8 days to 1 week, 14 days to 2 weeks.
 * For "full weeks only" semantics, consider Math.floor(diffDays/7) instead.
 *
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {number} Number of weeks (rounded)
 */
export const calculateWeeksBetweenDates = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.round(diffDays / 7);
};

/**
 * Recurring 2-week job posting duration rule:
 * End date can be at most 60 days after start date.
 */
export const getEndDateFieldErrorMax60Days = ({ value }) => {
  const baseError = getEndDateFieldError({ value });
  if (baseError) return baseError;

  if (!value) return 'error.field.empty';

  const startDate = new Date(value[0]);
  const endDate = new Date(value[1]);

  const startEpoch = startDate.getTime();
  const endEpoch = endDate.getTime();

  if (!startEpoch || !endEpoch) return 'error.date.invalid';

  const msPerDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((endEpoch - startEpoch) / msPerDay);

  return diffDays > 60 ? 'error.dates.max60Days' : undefined;
};

export const isValidDateRangeMax60Days = (value) =>
  getStartFieldError({ value }) || getEndDateFieldErrorMax60Days({ value });

/**
 * Calculate expected hours in a week based on job shifts
 * Note: If a job has X hours and X <= 8 hours, we count 8 hours, otherwise, we count X-0.5 hours.
 */
export const calculateExpectedHoursInWeek = (listOfJobs) =>
  listOfJobs.reduce((totalHours, job) => {
    const durationMs = job.endEpochMs - job.startEpochMs;
    const durationHours = durationMs / (1000 * 60 * 60);

    // If X <= 8 hours, count 8 hours; otherwise count X - 0.5 hours
    const adjustedHours = durationHours <= 8 ? 8 : durationHours - 0.5;

    return totalHours + adjustedHours;
  }, 0);

/**
 * Check if two time ranges overlap
 * Handles both same-day shifts and overnight shifts that may overlap with shifts on adjacent days
 */
export const doShiftsOverlap = (shift1, shift2) => {
  const start1 = shift1.startEpochMs;
  const end1 = shift1.endEpochMs;
  const start2 = shift2.startEpochMs;
  const end2 = shift2.endEpochMs;

  // Standard time range overlap check works for all cases:
  // - Same day shifts
  // - Overnight shifts overlapping with next day shifts
  // - Non-overlapping shifts
  // Two ranges overlap if: start1 < end2 && start2 < end1
  return start1 < end2 && start2 < end1;
};

/**
 * Convert component state to job order dates/times
 * @param {Object} week1Shifts - { dayIndex: { startTime: minutes, endTime: minutes, openings: number } }
 * @param {Object} week2Shifts - { dayIndex: { startTime: minutes, endTime: minutes, openings: number } }
 * @param {Date} week1StartDate - Start date for week 1
 * @param {Date} week2StartDate - Start date for week 2
 * @param {string} positionTemplateId - Position template ID
 * @returns {Array} Array of { startEpochMs, endEpochMs, positionTemplateId, numOpenings }
 */
export const convertToJobOrders = (
  week1Shifts,
  week2Shifts,
  week1StartDate,
  week2StartDate,
  positionTemplateId
) => {
  const jobOrders = [];

  // Process Week 1
  if (week1StartDate) {
    Object.keys(week1Shifts).forEach((dayIndexStr) => {
      const dayIndex = parseInt(dayIndexStr, 10);
      const shift = week1Shifts[dayIndex];
      if (!shift || shift.startTime == null || shift.endTime == null) return;

      const shiftDate = new Date(week1StartDate);
      const currentDay = shiftDate.getDay();
      // Handle wrap-around: if dayIndex < currentDay, add 7 to get next occurrence
      // e.g., Monday (1) to Sunday (0): (0 - 1 + 7) % 7 = 6 days forward
      const daysToAdd = (dayIndex - currentDay + 7) % 7;
      shiftDate.setDate(shiftDate.getDate() + daysToAdd);

      const startTime = minutesFromMidnightToTime(shift.startTime);
      const endTime = minutesFromMidnightToTime(shift.endTime);

      const startDateTime = new Date(shiftDate);
      startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

      const endDateTime = new Date(shiftDate);
      endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

      // Handle overnight shifts
      if (endDateTime < startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      jobOrders.push({
        startEpochMs: startDateTime.getTime(),
        endEpochMs: endDateTime.getTime(),
        positionTemplateId,
        numOpenings: shift.openings ?? 1,
      });
    });
  }

  // Process Week 2
  if (week2StartDate) {
    Object.keys(week2Shifts).forEach((dayIndexStr) => {
      const dayIndex = parseInt(dayIndexStr, 10);
      const shift = week2Shifts[dayIndex];
      if (!shift || shift.startTime == null || shift.endTime == null) return;

      const shiftDate = new Date(week2StartDate);
      const currentDay = shiftDate.getDay();
      // Handle wrap-around: if dayIndex < currentDay, add 7 to get next occurrence
      // e.g., Monday (1) to Sunday (0): (0 - 1 + 7) % 7 = 6 days forward
      const daysToAdd = (dayIndex - currentDay + 7) % 7;
      shiftDate.setDate(shiftDate.getDate() + daysToAdd);

      const startTime = minutesFromMidnightToTime(shift.startTime);
      const endTime = minutesFromMidnightToTime(shift.endTime);

      const startDateTime = new Date(shiftDate);
      startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

      const endDateTime = new Date(shiftDate);
      endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

      // Handle overnight shifts
      if (endDateTime < startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      jobOrders.push({
        startEpochMs: startDateTime.getTime(),
        endEpochMs: endDateTime.getTime(),
        positionTemplateId,
        numOpenings: shift.openings ?? 1,
      });
    });
  }

  return jobOrders;
};

/**
 * Detect overlaps in job orders
 * @param {Array} jobOrders - Array of { startEpochMs, endEpochMs, ... }
 * @returns {Object} { hasOverlap: boolean, overlappingPairs: Array }
 */
export const detectOverlaps = (jobOrders) => {
  const overlappingPairs = [];

  for (let i = 0; i < jobOrders.length; i += 1) {
    for (let j = i + 1; j < jobOrders.length; j += 1) {
      const shift1 = jobOrders[i];
      const shift2 = jobOrders[j];

      if (doShiftsOverlap(shift1, shift2)) {
        overlappingPairs.push([shift1, shift2]);
      }
    }
  }

  return {
    hasOverlap: overlappingPairs.length > 0,
    overlappingPairs,
  };
};
